/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import Description from "@mui/icons-material/Description";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import EditNoteIcon from "@mui/icons-material/EditNote";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PublicIcon from "@mui/icons-material/Public";
import ListItemIcon from "@mui/material/ListItemIcon"; // Ajouté pour les icônes dans MenuItem
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import CssIcon from "@mui/icons-material/Css";
import ImageIcon from "@mui/icons-material/Image";

import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { statusLabels, VALIDATED, PUBLISHED } from "contants";
import SourceActions from "redux-react/actions/sourceActions";
import { updateAsset } from "redux-react/reducers/assetReducer";
import { display } from "redux-react/reducers/snackBarReducer";
import Code from "@mui/icons-material/Code";
import Share from "@mui/icons-material/Share";
import FaiqIntegration from "./Columns/FaiqIntegration";
import FaiqIntegrationHelp from "./Columns/FaiqIntegration/FaiqIntegrationHelp";
import MDTypography from "components/Basics/MDTypography";
import { AccountTreeRounded } from "@mui/icons-material";
import AccessChip from "./AccessChip";

const getStatusColor = value => {
	if (value === "draft") return "warning";
	if (value === "published") return "success";
	return "info";
};

const getDocumentIcon = (type, isSelected) => {
	const color = isSelected ? "white" : "secondary";
	switch (type) {
		case "theme":
			return <AccountTreeRounded fontSize="medium" color={color} />;
		case "documents":
			return <Description fontSize="medium" color={color} />;
		case "WebPage":
			return <PublicIcon fontSize="medium" color={color} />;
		case "sitemap":
			return <Share fontSize="medium" color={color} />;
		case "htmlText":
			return <Code fontSize="medium" color={color} />;
		case "videos":
			return <OndemandVideoIcon fontSize="medium" color={color} />;
		case "css":
			return <CssIcon fontSize="medium" color={color} />;
		case "images":
			return <ImageIcon fontSize="medium" color={color} />;
		default:
			return <Description fontSize="medium" color={color} />;
	}
};

const statusIcons = {
	[PUBLISHED]: <PublicIcon fontSize="small" />,
	[VALIDATED]: <TaskAltIcon fontSize="small" />
};

/**
 * Generates columns configuration for the asset table.
 *
 * @param {function} t - The translation function used to get the localized text.
 * @param {Object} param1 - The object containing roles for the asset columns.
 * @param {boolean} param1.isWritter - Indicates if the user is a writer.
 * @param {boolean} param1.isReviewer - Indicates if the user is a reviewer.
 * @param {boolean} param1.isPublisher - Indicates if the user is a publisher.
 * @returns {Array} - An array of column configuration objects.
 */
const assetColumns = (
	t,
	{ isWritter, isReviewer, isPublisher, canChangeStatus, reloadTable = () => {} }
) => [
	{
		Header: t("ASSETS.TABLE.name"),
		accessor: "name",
		cellWidth: "30vw",
		boxStyle: {
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap"
		},
		Cell: ({ value, row, isSelected }) => {
			return (
				<MDTypography fontWeight="bold" variant="button" color={isSelected ? "white" : "info"}>
					{value}
				</MDTypography>
			);
		}
	},
	{
		Header: t("ASSETS.TABLE.fileType"),
		accessor: "fileType",
		Cell: ({ value, isSelected }) => (
			<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
				<Tooltip placement="left" title={t(`ASSETS.FILETYPE.${value}`, { defaultValue: value })}>
					{getDocumentIcon(value, isSelected)}
				</Tooltip>
			</div>
		)
	},
	{
		Header: t("ASSETS.TABLE.status") || t("SOURCES.FILES.status"),
		accessor: "status",
		Cell: ({ row, isSelected }) => {
			return (
				<Status value={row.original} t={t} disabled={!canChangeStatus} isSelected={isSelected} />
			);
		}
	},
	{
		Header: t("ASSETS.TABLE.access") || t("ASSETS.DETAILS.access"),
		accessor: "access",
		Cell: ({ row, isSelected }) => {
			return <Access value={row.original} t={t} isSelected={isSelected} />;
		}
	},
	{
		Header: "FAIQ",
		accessor: "faiq",
		Cell: ({ row }) => {
			return <FaiqIntegration value={row.original} />;
		},
		/**
		 * "Help" content will be displayed in a dialog when user clicks
		 * on "i" icon in the header of the column. The "i" will appear automatically
		 * when the column has a "Help" property defined.
		 */
		Help: () => {
			return <FaiqIntegrationHelp />;
		}
	},
	{
		Header: t("ASSETS.TABLE.createdAt"),
		accessor: "createdAt",
		Cell: ({ value }) => {
			const formattedDate = value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
			return <p>{formattedDate}</p>;
		}
	},
	{
		Header: t("ASSETS.TABLE.updatedAt"),
		accessor: "updatedAt",
		Cell: ({ value }) => {
			return <p>{value ? moment(value).format("DD/MM/YYYY HH:mm") : "-"}</p>;
		}
	}
];

/**
 * @param {Object} param0 - Component props.
 * @param {obj} param0.value - The object that contains row original value
 * @param {function} param0.t - The translation function used to get the localized label for the status.
 * @param {boolean} param0.disabled - Indicates if the `Chip` component should be disabled.
 * @returns {JSX.Element} The rendered `Chip` component.
 */
function Status({ value: original, t, disabled, isSelected }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const dispatch = useDispatch();
	const open = Boolean(anchorEl);
	const { _id, status, assistantID } = original;

	const handleClickListItem = e => {
		e.stopPropagation();
		e.preventDefault();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuItemClick = selected => {
		setAnchorEl(null);
		const onSuccessCallback = res => {
			dispatch(
				display({
					type: "success",
					message: t("SOURCES.FILES.updateSuccess")
				})
			);
			dispatch(updateAsset({ ...res, id: res._id }));
		};
		dispatch(SourceActions.updateAsset(_id, { status: selected }, onSuccessCallback));
	};

	const handleClose = e => {
		e.stopPropagation();
		e.preventDefault();
		setAnchorEl(null);
	};

	const label = t(`ASSETS.STATUS.${status}`, { defaultValue: status });

	return (
		<>
			<Chip
				label={label}
				size="small"
				color={getStatusColor(status)}
				variant={isSelected ? "filled" : "outlined"}
				onClick={handleClickListItem}
				icon={statusIcons[status] || <EditNoteIcon fontSize="small" />}
				disabled={disabled}
			/>
			<Menu
				id="lock-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{
					"aria-labelledby": "lock-button",
					role: "listbox"
				}}
			>
				{statusLabels.map(option => (
					<MenuItem
						key={option.value}
						disabled={option.value === status}
						selected={option.value === status}
						onClick={e => {
							e.stopPropagation();
							e.preventDefault();
							handleMenuItemClick(option.value);
						}}
					>
						<ListItemIcon>
							{statusIcons[option.value] || <EditNoteIcon fontSize="small" />}
						</ListItemIcon>
						{t(option.label)}
					</MenuItem>
				))}
			</Menu>
		</>
	);
}

/**
 * @param {Object} param0 - Component props.
 * @param {obj} param0.value - The object that contains row original value
 * @param {function} param0.t - The translation function used to get the localized label for the status.
 * @returns {JSX.Element} The rendered `Chip` component.
 */
function Access({ value: original, t, isSelected }) {
	const { _id, access, format } = original;
	return (
		<AccessChip
			isSelected={isSelected}
			access={access || "private"}
			format={format}
			_id={_id}
			allowChange
		/>
	);
}

export default assetColumns;
